/**
 * CoT Event Playback (dev only)
 *
 * Drives the real AgentAssistantMessage + AgentChainOfThought through a
 * paced async generator, one PlatformEvent at a time. Lets us feed recorded
 * or synthetic event streams — including intentionally reordered ones — and
 * watch the CoT render without needing a live backend.
 *
 * See context/plan/twinkling-leaping-pumpkin.md for design.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  type ChatModelAdapter,
  type ChatModelRunResult,
} from '@assistant-ui/react';
import { Thread } from '@assistant-ui/react-ui';
import '@assistant-ui/react-ui/styles/index.css';
import '@assistant-ui/react-ui/styles/markdown.css';
import { PlatformEventContext } from '@/contexts/PlatformEventContext';
import type { SubscribeFn } from '@/contexts/PlatformEventContext';
import { CanvasProvider } from '@/contexts/CanvasContext';
import { ThoughtsProvider } from '@/contexts/ThoughtsContext';
import { AgentAssistantMessage } from '@/components/assistant-ui/agent-assistant-message';
import { AgentUserMessage } from '@/components/assistant-ui/agent-user-message';
import { AnchoredViewport } from '@/components/assistant-ui/anchored-viewport';
import { TimelineEntryRow } from '@/components/assistant-ui/cot-timeline';
import { AnimatedListItem } from '@/components/ui/AnimatedListItem';
import { useDelegatedTaskStatus } from '@/hooks/useDelegatedTaskStatus';
import { AnimatePresence } from 'motion/react';
import {
  createRunStreamState,
  applyRunEvent,
  buildRunContent,
  type RunStreamState,
} from '@/utils/run-stream-state';
import { runStateStore } from '@/hooks/useAsyncInvokeAdapter';
import type {
  PlatformEvent,
  RunFinalAnswerDeltaData,
  EventFilter,
} from '@/types/events';
import type { Canvas, Surface } from '@/types/canvases';
import { FIXTURES, CAPTURE_FIXTURES, loadImportedFixture, type PlaybackFixture } from './cot-playback-fixtures';
import { reorderEvents, type PlaybackMode, PLAYBACK_MODES } from './cot-playback-reorder';

// ─── Fake context values ────────────────────────────────────────────────────

/** Hook that exposes a `PlatformEventContextValue` driven by a manual
 *  `dispatch(event)` instead of WebSocket frames. The page calls `dispatch`
 *  every time the playback advances, so any subscriber (currently the
 *  task-card preview's `useDelegatedTaskStatus`) sees the same event stream
 *  as the conversation thread adapter. */
function usePlaybackPlatformEvents() {
  type Sub = { filters: EventFilter[]; handler: (event: PlatformEvent) => void };
  const subsRef = useRef<Set<Sub>>(new Set());

  const subscribe: SubscribeFn = useCallback((filters, handler) => {
    const sub: Sub = { filters, handler };
    subsRef.current.add(sub);
    return () => { subsRef.current.delete(sub); };
  }, []);

  const dispatch = useCallback((event: PlatformEvent) => {
    for (const sub of subsRef.current) {
      if (eventMatchesFilters(event, sub.filters)) {
        try { sub.handler(event); } catch (e) { console.error('[dev playback] handler threw', e); }
      }
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      subscribe,
      isConnected: true,
      diagnostics: { connectedSince: null, disconnectCount: 0, lastDisconnectReason: null, lastDisconnectTime: null, reconnectAttempts: 0 },
    }),
    [subscribe],
  );

  return { contextValue, dispatch };
}

function eventMatchesFilters(event: PlatformEvent, filters: EventFilter[]): boolean {
  if (filters.length === 0) return true;
  return filters.some((f) => filterMatches(event, f));
}

function filterMatches(event: PlatformEvent, f: EventFilter): boolean {
  if (f.eventType) {
    if (f.eventType.endsWith('.*')) {
      const prefix = f.eventType.slice(0, -1); // drops the '*' but keeps the trailing '.'
      if (!event.eventType.startsWith(prefix)) return false;
    } else if (f.eventType !== event.eventType) {
      return false;
    }
  }
  if (f.runId) {
    const data = event.data as Record<string, unknown> | undefined;
    const dataRunId = data?.runId as string | undefined;
    const dataParentRunId = data?.parentRunId as string | undefined;
    const matches =
      event.subjectId === f.runId
      || dataRunId === f.runId
      || (f.includeSubagents === true && dataParentRunId === f.runId);
    if (!matches) return false;
  }
  if (f.subjectId && event.subjectId !== f.subjectId) return false;
  if (f.source && event.source !== f.source) return false;
  return true;
}

const CONV_ID = 'con-playback';
const CVS_ID = 'cvs-playback';

const fakeCanvas: Canvas = {
  canvasId: CVS_ID,
  conversationId: CONV_ID,
  title: 'Playback Canvas',
  isDefault: true,
  layout: 'stack',
  createdAt: new Date().toISOString(),
};
const fakeSurfaces: Surface[] = [];

// ─── Adapter factory ────────────────────────────────────────────────────────

/**
 * Returns a ChatModelAdapter whose `run()` loops awaiting `getNextEvent()`
 * for each event it should process. Mirrors the inner loop of
 * `useAsyncInvokeAdapter` but with no network, no WS, and no terminal grace.
 */
function createPlaybackAdapter(
  getNextEvent: () => Promise<PlatformEvent | null>,
  playbackRunId: string,
): ChatModelAdapter {
  return {
     
    run: async function* (): AsyncGenerator<ChatModelRunResult> {
      // Initial yield: tag with the synthetic runId so the CoT renderer
      // subscribes to this playback session's slot in runStateStore.
      yield {
        content: [{ type: 'reasoning' as const, text: '' }],
        metadata: { custom: { runId: playbackRunId } },
      } as ChatModelRunResult;

      const state: RunStreamState = createRunStreamState();

      while (true) {
        const event = await getNextEvent();
        if (!event) {
          // Graceful end: yield whatever final state exists and finish.
          const durationMs = runStateStore.getSnapshot(playbackRunId).durationMs;
          yield {
            content: buildRunContent(state),
            status: { type: 'complete' as const, reason: 'stop' as const },
            metadata: {
              custom: {
                ...(durationMs != null && { runDurationMs: durationMs }),
                runId: playbackRunId,
              },
            },
          } as ChatModelRunResult;
          return;
        }

        applyRunEvent(state, event);
        runStateStore.updateLive(playbackRunId, state.iterations);

        if (state.hasFinalAnswer) runStateStore.updateProgress(playbackRunId, { hasFinalAnswer: true });
        if (event.eventType === 'agent.run.final_answer_delta') {
          const data = event.data as unknown as RunFinalAnswerDeltaData;
          const iter = state.iterations.get(data.iteration);
          if (iter?.finalAnswerText) runStateStore.updateProgress(playbackRunId, { hasTextContent: true });
        }

        if (state.terminal) {
          const t = state.terminal;
          runStateStore.updateProgress(playbackRunId, { isRunComplete: true });
          if (t.durationMs != null) runStateStore.updateDuration(playbackRunId, t.durationMs);

          // Intermediate yield so streaming view updates one last time
          yield {
            content: buildRunContent(state),
            metadata: { custom: { runId: playbackRunId } },
          } as ChatModelRunResult;

          // Mirror the failed branch of useAsyncInvokeAdapter: error text part
          // + incomplete status + errorCode metadata (drives ErrorAwareText).
          if (t.kind === 'failed') {
            yield {
              content: [
                ...Array.from(buildRunContent(state)),
                { type: 'text' as const, text: `Error: ${t.error}` },
              ],
              status: {
                type: 'incomplete' as const,
                reason: 'error' as const,
                error: t.error,
              },
              metadata: {
                custom: {
                  ...(t.durationMs != null && { runDurationMs: t.durationMs }),
                  ...(t.errorCode && { errorCode: t.errorCode }),
                  runId: playbackRunId,
                },
              },
            } as ChatModelRunResult;
            return;
          }

          // Keep draining like the production adapter: a parked ask_user run
          // (`awaiting_input`) emits a trailing completed + final_answer_*
          // tail, and fixtures may carry further events after a terminal to
          // exercise ordering (e.g. a poll-completed that an ask flips back).
          // Only `failed` short-circuits; everything else finishes on the
          // graceful end-of-events branch above.
          continue;
        }

        yield {
          content: buildRunContent(state),
          metadata: { custom: { runId: playbackRunId } },
        } as ChatModelRunResult;
      }
    },
  };
}

// ─── Task-card preview ─────────────────────────────────────────────────────

/** Stripped-down preview of {@link AuiDelegatedTaskCard} for the dev page.
 *  Uses the same `useDelegatedTaskStatus` hook so the timeline / status /
 *  duration / token counts go through the same code paths the production
 *  card does — but skips the canvas navigation, dropdown, and surface
 *  references the full card needs. */
function PlaybackTaskCard({
  delegatedRunId,
  agentName,
  prompt,
}: {
  delegatedRunId: string;
  agentName: string;
  prompt: string;
}) {
  const live = useDelegatedTaskStatus(delegatedRunId);
  const visibleEntries = useMemo(() => live.timelineEntries.slice(-3), [live.timelineEntries]);
  const elapsedSec = live.elapsedMs != null ? Math.round(live.elapsedMs / 1000) : null;
  const status = live.status ?? 'pending';
  const isRunning = status === 'running' || status === 'pending';

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/10 p-3 text-xs">
      <div className="flex items-center gap-2">
        <span className="font-medium text-foreground/90">{agentName}</span>
        <span className={`rounded px-2 py-0.5 text-[10px] uppercase tracking-wide ${
          status === 'completed' ? 'bg-foreground/10 text-foreground/70'
          : status === 'failed' || status === 'cancelled' ? 'bg-foreground/10 text-foreground/70'
          : 'bg-primary/20 text-primary'
        }`}>{status}</span>
      </div>
      <div className="line-clamp-2 text-foreground/80">{prompt}</div>
      {isRunning && (
        <div className="flex flex-col gap-0.5">
          <AnimatePresence initial={false}>
            {visibleEntries.map((entry) => (
              <AnimatedListItem key={entry.id}>
                <div className="py-0.5">
                  <TimelineEntryRow entry={entry} />
                </div>
              </AnimatedListItem>
            ))}
          </AnimatePresence>
        </div>
      )}
      {!isRunning && live.resultSummary && (
        <div className="line-clamp-3 text-foreground/70">{live.resultSummary}</div>
      )}
      <div className="flex items-center gap-3 text-foreground/50">
        {elapsedSec != null && <span>{elapsedSec}s</span>}
        {live.toolCallCount > 0 && <span>{live.toolCallCount} tools</span>}
        {live.usage && (
          <span>{live.usage.inputTokens} in / {live.usage.outputTokens} out</span>
        )}
      </div>
    </div>
  );
}

// ─── Thread wrapper ─────────────────────────────────────────────────────────

/**
 * Module-level "kicked" set — one entry per `runtimeKey`. React Strict
 * Mode double-invokes the mount effect; the first invocation records the
 * key here, and the second skips. Reset bumps runtimeKey so the new
 * runtime gets its own fresh kick.
 */
const kickedKeys = new Set<number>();

interface PlaybackThreadProps {
  getNextEvent: () => Promise<PlatformEvent | null>;
  runtimeKey: number;
  playbackRunId: string;
}

function PlaybackThread({ getNextEvent, runtimeKey, playbackRunId }: PlaybackThreadProps) {
  const adapter = useMemo<ChatModelAdapter>(
    () => createPlaybackAdapter(getNextEvent, playbackRunId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runtimeKey, getNextEvent, playbackRunId],
  );
  const runtime = useLocalRuntime(adapter);

  useEffect(() => {
    if (kickedKeys.has(runtimeKey)) return;
    kickedKeys.add(runtimeKey);
    runtime.thread.append({
      role: 'user',
      content: [{ type: 'text', text: 'Replaying recorded run' }],
    });
  }, [runtime, runtimeKey]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <Thread.Root>
        <AnchoredViewport>
          <Thread.Messages
            components={{
              UserMessage: AgentUserMessage,
              AssistantMessage: AgentAssistantMessage,
            }}
          />
        </AnchoredViewport>
        {/* ScrollToBottom lives inside a Thread.ViewportFooter OUTSIDE
            the viewport so the button stays at the visual bottom without
            competing with the AnchoredViewport's scroll. */}
        <Thread.ViewportFooter>
          <Thread.ScrollToBottom />
        </Thread.ViewportFooter>
      </Thread.Root>
    </AssistantRuntimeProvider>
  );
}

// ─── State inspector ────────────────────────────────────────────────────────

function StateInspector({
  eventIndex,
  totalEvents,
  currentEvent,
  fixture,
  playbackRunId,
}: {
  eventIndex: number;
  totalEvents: number;
  currentEvent: PlatformEvent | undefined;
  fixture: PlaybackFixture;
  playbackRunId: string;
}) {
  const subscribe = useCallback(
    (listener: () => void) => runStateStore.subscribe(playbackRunId, listener),
    [playbackRunId],
  );
  const getSnapshot = useCallback(
    () => runStateStore.getSnapshot(playbackRunId).live,
    [playbackRunId],
  );
  const liveSnapshot = useSyncExternalStore(subscribe, getSnapshot);

  const parentId = fixture.parentRunId;
  const childIds = fixture.childRunIds ?? [];
  const isChild = (ev: PlatformEvent | undefined): boolean => {
    if (!ev) return false;
    const data = ev.data as { parentRunId?: string };
    return !!data.parentRunId;
  };

  return (
    <div className="space-y-4 text-xs">
      <div>
        <div className="font-semibold uppercase tracking-wide text-muted-foreground">
          Event {eventIndex} / {totalEvents}
        </div>
        {currentEvent && (
          <div className="mt-1 space-y-0.5">
            <div>
              <span className="text-muted-foreground">type:</span>{' '}
              <code className="text-amber-300">{currentEvent.eventType}</code>
            </div>
            <div>
              <span className="text-muted-foreground">runId:</span>{' '}
              <code className={isChild(currentEvent) ? 'text-sky-300' : 'text-violet-300'}>
                {String((currentEvent.data as { runId?: string }).runId ?? currentEvent.subjectId ?? '?')}
              </code>
              {isChild(currentEvent) && <span className="ml-1 text-sky-500">(child)</span>}
            </div>
            <div>
              <span className="text-muted-foreground">parentRunId:</span>{' '}
              <code className="text-muted-foreground">
                {String((currentEvent.data as { parentRunId?: string }).parentRunId ?? '—')}
              </code>
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="font-semibold uppercase tracking-wide text-muted-foreground">
          runStateStore.live
        </div>
        <div className="mt-1">version: {liveSnapshot.version}</div>
        {liveSnapshot.iterations.length === 0 && (
          <div className="text-muted-foreground">(empty)</div>
        )}
        {liveSnapshot.iterations.map((it) => (
          <div key={it.index} className="mt-0.5">
            iter {it.index}: {it.toolCalls.length} tool calls
            {it.isThinking === true ? ' · thinking' : ''}
            {it.hasFinalAnswer ? ' · finalAnswer' : ''}
            {it.reasoningLen > 0 ? ` · rLen=${it.reasoningLen}` : ''}
            {it.responseLen > 0 ? ` · respLen=${it.responseLen}` : ''}
          </div>
        ))}
      </div>

      {parentId && childIds.length > 0 && (
        <div>
          <div className="font-semibold uppercase tracking-wide text-muted-foreground">
            Sub-agents ({childIds.length})
          </div>
          <div className="mt-1 space-y-0.5 text-muted-foreground">
            {childIds.map((cid) => (
              <div key={cid}>
                <code className="text-sky-300">{cid}</code>
              </div>
            ))}
          </div>
        </div>
      )}

      {currentEvent && (
        <details className="rounded border border-border p-2">
          <summary className="cursor-pointer select-none text-muted-foreground">
            raw event
          </summary>
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all text-[10px]">
            {JSON.stringify(currentEvent, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function CotPlaybackPage() {
  // A fixture captured live via __eventDebug.exportFixture() (localStorage),
  // prepended to the selector so a real stuck run can be replayed with no
  // source edit. Read once on mount.
  const importedFixture = useMemo(() => loadImportedFixture(), []);
  const fixtures = useMemo(
    () => [
      ...(importedFixture ? [importedFixture] : []),
      ...CAPTURE_FIXTURES,
      ...FIXTURES,
    ],
    [importedFixture],
  );

  const [fixtureId, setFixtureId] = useState<string>(fixtures[0].id);
  const fixture = useMemo(
    () => fixtures.find((f) => f.id === fixtureId) ?? fixtures[0],
    [fixtures, fixtureId],
  );

  const [mode, setMode] = useState<PlaybackMode>('in-order');
  const [shuffleSeed, setShuffleSeed] = useState<number>(1);

  // The effective event sequence, produced by applying the current mode to
  // the fixture's chronological events.
  const orderedEvents = useMemo(
    () => reorderEvents(fixture, mode, shuffleSeed),
    [fixture, mode, shuffleSeed],
  );

  const [eventIndex, setEventIndex] = useState(0);
  const [runtimeKey, setRuntimeKey] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speedMs, setSpeedMs] = useState(500);

  const resolverRef = useRef<((e: PlatformEvent | null) => void) | null>(null);

  // Platform-events fan-out so the task-card preview's
  // `useDelegatedTaskStatus` hook sees the same events as the conversation
  // adapter. Kept separate from the conversation runtime — that path is
  // already driven by `getNextEvent` resolution.
  const { contextValue: platformEventsValue, dispatch: dispatchPlatformEvent } = usePlaybackPlatformEvents();
  const dispatchRef = useRef(dispatchPlatformEvent);
  dispatchRef.current = dispatchPlatformEvent;

  // The synthetic runId this playback session tags its message with. Task
  // events are re-stamped to it at dispatch time (see stepForward) so the
  // in-CoT ConversationRunTaskList — which subscribes by the message's runId —
  // receives them, exactly as it does in a real conversation. Set in render
  // once playbackRunId is computed below.
  const playbackRunIdRef = useRef('');

  const getNextEvent = useCallback(
    () =>
      new Promise<PlatformEvent | null>((resolve) => {
        resolverRef.current = resolve;
      }),
    [],
  );

  const stepForward = useCallback(() => {
    setEventIndex((idx) => {
      if (idx >= orderedEvents.length) {
        resolverRef.current?.(null);
        resolverRef.current = null;
        return idx;
      }
      const event = orderedEvents[idx];
      resolverRef.current?.(event);
      resolverRef.current = null;
      // Fan the same event out to platform-event subscribers (task-card
      // preview + in-CoT task list). Synchronous so both renderers update
      // together. Task events are re-stamped to this session's synthetic runId
      // so the in-CoT ConversationRunTaskList (keyed on the message runId)
      // matches — mirroring production where message runId === event runId.
      const isTaskEvent =
        event.eventType.startsWith('task.') ||
        event.eventType.startsWith('agent.run.agent_task_');
      const forDispatch = isTaskEvent
        ? {
            ...event,
            subjectId: playbackRunIdRef.current,
            data: {
              ...(event.data as Record<string, unknown>),
              runId: playbackRunIdRef.current,
            },
          }
        : event;
      dispatchRef.current(forDispatch);
      return idx + 1;
    });
  }, [orderedEvents]);

  const reset = useCallback(() => {
    resolverRef.current?.(null);
    resolverRef.current = null;
    setEventIndex(0);
    setPlaying(false);
    setRuntimeKey((k) => k + 1);
  }, []);

  // Auto-play
  useEffect(() => {
    if (!playing) return;
    if (eventIndex >= orderedEvents.length) {
      setPlaying(false);
      // Signal end-of-events so the adapter finishes its message. The drain
      // no longer returns at the first terminal (parked ask_user runs carry a
      // trailing completed/final_answer tail), so it awaits one more event
      // after the last — resolve it with null instead of hanging.
      resolverRef.current?.(null);
      resolverRef.current = null;
      return;
    }
    const t = setTimeout(stepForward, speedMs);
    return () => clearTimeout(t);
  }, [playing, speedMs, stepForward, eventIndex, orderedEvents.length]);

  // ArrowRight steps forward. Ignored when focus is in an editable element
  // (the fixture / mode selects, the speed slider, etc.).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'ArrowRight') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
      e.preventDefault();
      stepForward();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stepForward]);

  // Auto-reset when fixture / mode / seed changes, but NOT on first mount
  // (a mount-time reset would race with Strict Mode's double-invoke of
  // this effect and double-bump runtimeKey).
  const skipFirstResetRef = useRef(true);
  const resetRef = useRef(reset);
  resetRef.current = reset;
  useEffect(() => {
    if (skipFirstResetRef.current) {
      skipFirstResetRef.current = false;
      return;
    }
    resetRef.current();
  }, [fixtureId, mode, shuffleSeed]);

  const currentEvent =
    eventIndex > 0 && eventIndex - 1 < orderedEvents.length
      ? orderedEvents[eventIndex - 1]
      : undefined;

  // Synthetic runId per playback session — bumps on Reset along with runtimeKey.
  const playbackRunId = `rdr-playback-${runtimeKey}`;
  playbackRunIdRef.current = playbackRunId;

  // The fixture's actual runId (subjectId on its events). The task-card hook
  // matches subscriptions against `data.runId` / `subjectId` so the card
  // must subscribe by the fixture's id, not the synthetic playback id.
  const fixtureRunId = useMemo(() => {
    const first = fixture.events[0];
    return (
      (first?.data as Record<string, unknown> | undefined)?.runId as string | undefined
      ?? first?.subjectId
      ?? 'fixture-run'
    );
  }, [fixture]);
  const fixtureAgentName = useMemo(() => {
    for (const e of fixture.events) {
      const name = (e.data as Record<string, unknown> | undefined)?.agentName;
      if (typeof name === 'string') return name;
    }
    return 'Agent';
  }, [fixture]);
  const fixturePrompt = useMemo(() => {
    for (const e of fixture.events) {
      const prompt = (e.data as Record<string, unknown> | undefined)?.prompt;
      if (typeof prompt === 'string') return prompt;
    }
    return fixture.title;
  }, [fixture]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-[1600px] px-6 py-8">
        <header className="mb-6 border-b border-border pb-4">
          <h1 className="text-2xl font-bold">CoT Event Playback</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Feed recorded or synthetic <code>agent.run.*</code> events into the
            real Chain-of-Thought pipeline, one at a time. Dev only.
          </p>
        </header>

        {/* Controls bar */}
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-border p-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Fixture:</span>
            <select
              className="rounded border border-border bg-background px-2 py-1"
              value={fixtureId}
              onChange={(e) => setFixtureId(e.target.value)}
            >
              {fixtures.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.title} ({f.events.length} events)
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Mode:</span>
            <select
              className="rounded border border-border bg-background px-2 py-1"
              value={mode}
              onChange={(e) => setMode(e.target.value as PlaybackMode)}
            >
              {PLAYBACK_MODES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>

          {mode === 'shuffled' && (
            <label className="flex items-center gap-2">
              <span className="text-muted-foreground">Seed:</span>
              <input
                type="number"
                className="w-20 rounded border border-border bg-background px-2 py-1"
                value={shuffleSeed}
                onChange={(e) => setShuffleSeed(Number(e.target.value) || 0)}
              />
            </label>
          )}

          <div className="flex items-center gap-1">
            <button
              type="button"
              className="rounded bg-primary px-3 py-1 text-primary-foreground disabled:opacity-40"
              onClick={stepForward}
              disabled={eventIndex >= orderedEvents.length}
              title="Step forward (→)"
            >
              Step ▶︎
            </button>
            <button
              type="button"
              className="rounded border border-border px-3 py-1"
              onClick={() => setPlaying((p) => !p)}
              disabled={eventIndex >= orderedEvents.length && !playing}
            >
              {playing ? 'Pause' : 'Play'}
            </button>
            <button
              type="button"
              className="rounded border border-border px-3 py-1"
              onClick={reset}
            >
              Reset
            </button>
          </div>

          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Speed:</span>
            <input
              type="range"
              min={50}
              max={2000}
              step={50}
              value={speedMs}
              onChange={(e) => setSpeedMs(Number(e.target.value))}
              className="w-32"
            />
            <span className="tabular-nums text-muted-foreground">{speedMs}ms</span>
          </label>

          <div className="ml-auto tabular-nums text-muted-foreground">
            {eventIndex} / {orderedEvents.length}
          </div>
        </div>

        <p className="mb-4 text-xs text-muted-foreground">{fixture.description}</p>

        {/* Two-column layout */}
        <PlatformEventContext.Provider value={platformEventsValue}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
            <CanvasProvider
              conversationId={CONV_ID}
              initialCanvas={{ canvas: fakeCanvas, surfaces: fakeSurfaces }}
            >
              <ThoughtsProvider>
                <div key={runtimeKey} className="h-[640px] overflow-hidden rounded-lg border border-border">
                  <PlaybackThread
                    getNextEvent={getNextEvent}
                    runtimeKey={runtimeKey}
                    playbackRunId={playbackRunId}
                  />
                </div>
              </ThoughtsProvider>
            </CanvasProvider>

            <aside className="flex flex-col gap-4">
              <div className="rounded-lg border border-border bg-muted/20 p-4">
                <StateInspector
                  eventIndex={eventIndex}
                  totalEvents={orderedEvents.length}
                  currentEvent={currentEvent}
                  fixture={fixture}
                  playbackRunId={playbackRunId}
                />
              </div>
              <div>
                <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Task-card preview
                </h2>
                <PlaybackTaskCard
                  key={runtimeKey}
                  delegatedRunId={fixtureRunId}
                  agentName={fixtureAgentName}
                  prompt={fixturePrompt}
                />
              </div>
            </aside>
          </div>
        </PlatformEventContext.Provider>
      </div>
    </div>
  );
}
